/* eslint-disable no-nested-ternary */
import React, { useMemo, useState } from 'react';
import { Flex, Spinner, Text, Box, HStack, Tooltip } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import moment from 'moment';
import 'moment/locale/pt-br';
import {
  ChartLineUp,
  ArrowUp,
  ArrowDown,
  Equals,
  CheckCircle,
  Warning,
  XCircle,
} from 'phosphor-react';
import { useDebounceCallback } from 'usehooks-ts';
import { ReturnButton } from '../../../../components/buttons/ReturnButton';
import { IData, IPagination } from '../../../../types/shared';
import { IVarianciaNota, IVarianciaNotaKpi, useCompanies } from '../../../../hooks/useCompanies';
import { cleanFilters } from '../../../../utils/HandleFunctions';
import { Paginator } from '../../../../components/paginator';
import { FormBox } from '../../../../components/formBox';
import { useMonthFilter } from '../../../../hooks/useMonthFilter';
import { MonthNavigator } from '../../../../components/shared/MonthNavigator';
import { SearchModeBar, MODES_ID_NOME_CNPJ } from '../../../../components/SearchModeBar';

moment.locale('pt-br');

const MAPPED_CODI_ESP = [36, 38, 39, 44, 41, 47];

export function ModulesHomeFiscalVarianciaNotaPage() {
  const { getVarianciasNotas, getVarianciasNotasKpi } = useCompanies();

  const {
    currentDate: currentFilterDate,
    handlePrevMonth,
    handleNextMonth,
    handleSelectMonth,
    filters,
    setFilters,
  } = useMonthFilter({
    defaultToPreviousMonth: false,
  });

  const [searchMode, setSearchMode] = useState<'Nome' | 'ID' | 'CNPJ'>('Nome');
  const [searchText, setSearchText] = useState(
    () => filters.get('search') || filters.get('external_id') || filters.get('cnpj') || '',
  );

  const debouncedSearch = useDebounceCallback((value: string) => {
    const newFilters = new URLSearchParams(filters);
    newFilters.delete('search');
    newFilters.delete('external_id');
    newFilters.delete('cnpj');
    if (value) {
      if (searchMode === 'ID') {
        newFilters.set('external_id', value);
      } else if (searchMode === 'CNPJ') {
        newFilters.set('cnpj', value.replace(/[^0-9A-Za-z]/g, '').toUpperCase());
      } else {
        newFilters.set('search', value);
      }
    }
    newFilters.set('page', '1');
    setFilters(newFilters);
  }, 500);

  const filtersObject = useMemo(() => {
    const raw = Object.fromEntries([...Array.from(filters)]);
    const refMonth = moment(raw.date_start);
    const prevMonth = refMonth.clone().subtract(1, 'month');
    return cleanFilters({
      ...raw,
      date_end: refMonth.format('YYYY-MM-DD'),
      date_start: prevMonth.startOf('month').format('YYYY-MM-DD'),
    });
  }, [filters]);

  const kpiFiltersObject = useMemo(
    () =>
      cleanFilters({
        date_start: filtersObject.date_start,
        date_end: filtersObject.date_end,
      }),
    [filtersObject.date_start, filtersObject.date_end],
  );

  const queryKey = ['getVarianciasNotas', filtersObject];

  const { data: dataVariancias, isFetching } = useQuery<IData<IVarianciaNota[]>>({
    queryKey,
    queryFn: getVarianciasNotas,
  });

  const { data: kpiData, isFetching: isFetchingKpi } = useQuery<{
    data: IVarianciaNotaKpi;
  }>({
    queryKey: ['getVarianciasNotasKpi', kpiFiltersObject],
    queryFn: getVarianciasNotasKpi,
  });

  const { items: variancias, ...pagination } = dataVariancias?.data ?? {
    items: [],
    ...({} as IPagination),
  };

  const getVariacaoColor = (variacao: number) => {
    if (variacao <= 20) return '#10B981';
    if (variacao <= 50) return '#F59E0B';
    return '#EF4444';
  };

  const getVariacaoBg = (variacao: number) => {
    if (variacao <= 20) return '#F0FDF4';
    if (variacao <= 50) return '#FEFCE8';
    return '#FEF2F2';
  };

  const getVariacaoIcon = (atual: number, anterior: number) => {
    if (atual > anterior) return <ArrowUp size={14} color="#EF4444" weight="bold" />;
    if (atual < anterior) return <ArrowDown size={14} color="#10B981" weight="bold" />;
    return <Equals size={14} color="#6B7280" weight="bold" />;
  };

  const getCodEspLabel = (codiEsp: number | null) => {
    switch (codiEsp) {
      case 36:
        return 'NFE';
      case 38:
        return 'CTE';
      case 39:
      case 44:
        return 'NFSe';
      case 41:
      case 47:
        return 'NFCe';
      default:
        return '';
    }
  };

  const groupedVariancias = useMemo(() => {
    const groups: Record<string, IVarianciaNota[]> = {};
    variancias
      ?.filter(item => item.codi_esp !== null && MAPPED_CODI_ESP.includes(item.codi_esp))
      .forEach(item => {
        const key = item.codi_emp ?? 'unknown';
        if (!groups[key]) groups[key] = [];
        groups[key].push(item);
      });
    return Object.entries(groups);
  }, [variancias]);

  const statusCounts = kpiData?.data ?? { ok: 0, aviso: 0, erro: 0 };

  const mesParaLabel = currentFilterDate.format('MMMM/YYYY');
  const mesDeLabel = currentFilterDate.clone().subtract(1, 'month').format('MMMM/YYYY');

  return (
    <div className="flex w-full flex-col gap-2">
      <div className="flex flex-col gap-3 py-5">
        <div className="flex content-start md:w-1/3 lg:content-center">
          <ReturnButton previousPage="modulesHome/integration" />
          <div className="h-full">
            <div className="flex items-center justify-center rounded-full bg-[#E0F2FE] p-3">
              <ChartLineUp color="#0284C7" className="size-8" weight="duotone" />
            </div>
          </div>
          <div className="flex w-full flex-col justify-center gap-1 pl-2">
            <Text fontWeight="bold" fontSize="16px">
              Variância de Notas
            </Text>
            <Text fontSize="12px" color="#718EBF">
              Comparativo mês a mês por empresa
            </Text>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <Box mb={4} p={4} bg="gray.50" borderRadius="lg">
        <Flex align="flex-end" gap={3} flexWrap="wrap">
          <Box flex={1} minW="300px">
            <Text fontSize="xs" fontWeight="bold" color="gray.500" mb={1}>
              BUSCAR
            </Text>
            <HStack spacing={2}>
              <SearchModeBar
                modes={MODES_ID_NOME_CNPJ}
                mode={searchMode}
                onModeChange={m => setSearchMode(m as 'Nome' | 'ID' | 'CNPJ')}
                value={searchText}
                onValueChange={v => {
                  setSearchText(v);
                  debouncedSearch(v);
                }}
              />
            </HStack>
          </Box>
          <Box>
            <Text fontSize="xs" fontWeight="bold" color="gray.500" mb={1}>
              MÊS DE REFERÊNCIA
            </Text>
            <MonthNavigator
              currentDate={currentFilterDate}
              onPrevMonth={handlePrevMonth}
              onNextMonth={handleNextMonth}
              onSelectMonth={handleSelectMonth}
            />
          </Box>
        </Flex>
      </Box>

      {/* Referência */}
      <Box px={2} mb={2}>
        <Text fontSize="sm" color="gray.600">
          Comparando{' '}
          <Text as="span" fontWeight="bold" textTransform="capitalize">
            {mesDeLabel}
          </Text>{' '}
          vs{' '}
          <Text as="span" fontWeight="bold" textTransform="capitalize">
            {mesParaLabel}
          </Text>
        </Text>
      </Box>

      {/* Cards de resumo */}
      <Flex gap={3} px={2} mb={2} flexWrap="wrap">
        <Tooltip label="Contagem por tipo de nota (NFE, CTE, NFSe, NFCe), não por empresa" hasArrow>
          <Flex
            align="center"
            gap={2}
            px={4}
            py={3}
            bg="#F0FDF4"
            border="1px solid"
            borderColor="#10B981"
            borderRadius="lg"
            flex={1}
            minW="150px"
            cursor="default"
          >
            <CheckCircle size={24} color="#10B981" weight="fill" />
            <Box>
              <Text fontSize="xs" color="#10B981" fontWeight="bold">
                OK
              </Text>
              {isFetchingKpi ? (
                <Spinner size="sm" color="green.500" />
              ) : (
                <Text fontSize="lg" fontWeight="bold" color="#10B981">
                  {statusCounts.ok}
                </Text>
              )}
              <Text fontSize="xs" color="gray.500">
                Até 20% de variação
              </Text>
            </Box>
          </Flex>
        </Tooltip>
        <Tooltip label="Contagem por tipo de nota (NFE, CTE, NFSe, NFCe), não por empresa" hasArrow>
          <Flex
            align="center"
            gap={2}
            px={4}
            py={3}
            bg="#FEFCE8"
            border="1px solid"
            borderColor="#F59E0B"
            borderRadius="lg"
            flex={1}
            minW="150px"
            cursor="default"
          >
            <Warning size={24} color="#F59E0B" weight="fill" />
            <Box>
              <Text fontSize="xs" color="#F59E0B" fontWeight="bold">
                AVISO
              </Text>
              {isFetchingKpi ? (
                <Spinner size="sm" color="yellow.500" />
              ) : (
                <Text fontSize="lg" fontWeight="bold" color="#F59E0B">
                  {statusCounts.aviso}
                </Text>
              )}
              <Text fontSize="xs" color="gray.500">
                De 20% a 50% de variação
              </Text>
            </Box>
          </Flex>
        </Tooltip>
        <Tooltip label="Contagem por tipo de nota (NFE, CTE, NFSe, NFCe), não por empresa" hasArrow>
          <Flex
            align="center"
            gap={2}
            px={4}
            py={3}
            bg="#FEF2F2"
            border="1px solid"
            borderColor="#EF4444"
            borderRadius="lg"
            flex={1}
            minW="150px"
            cursor="default"
          >
            <XCircle size={24} color="#EF4444" weight="fill" />
            <Box>
              <Text fontSize="xs" color="#EF4444" fontWeight="bold">
                ERRO
              </Text>
              {isFetchingKpi ? (
                <Spinner size="sm" color="red.500" />
              ) : (
                <Text fontSize="lg" fontWeight="bold" color="#EF4444">
                  {statusCounts.erro}
                </Text>
              )}
              <Text fontSize="xs" color="gray.500">
                Acima de 50% de variação
              </Text>
            </Box>
          </Flex>
        </Tooltip>
      </Flex>

      {/* Lista */}
      <FormBox>
        <div className="flex flex-col gap-4 p-4">
          {isFetching ? (
            <div className="flex flex-col items-center gap-4">
              <Spinner size="lg" color="primary" />
              <span className="text-xl font-semibold">Carregando...</span>
            </div>
          ) : variancias?.length === 0 ? (
            <div className="flex flex-col items-center gap-4">Nenhum item encontrado</div>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                {groupedVariancias.map(([codiEmp, items]) => {
                  const nomeEmpresa = items[0]?.nome_empresa;
                  const maxVariacao = Math.max(...items.map(i => i.variacao_notas_total ?? 0));

                  return (
                    <div
                      key={codiEmp}
                      className="rounded-lg border border-gray-200 bg-white shadow-sm transition-shadow hover:shadow-md"
                    >
                      {/* Header da empresa */}
                      <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
                        <div className="flex flex-col">
                          <Text fontSize="sm" fontWeight="bold" color="gray.800">
                            {nomeEmpresa ?? `Empresa ${codiEmp}`}
                          </Text>
                          <Text fontSize="xs" color="gray.500">
                            ID {codiEmp}
                          </Text>
                        </div>
                        <Flex
                          alignItems="center"
                          gap={1}
                          px={2}
                          py={1}
                          borderRadius="full"
                          bg={getVariacaoBg(maxVariacao)}
                          border="1px solid"
                          borderColor={getVariacaoColor(maxVariacao)}
                        >
                          <Text
                            fontSize="xs"
                            fontWeight="bold"
                            color={getVariacaoColor(maxVariacao)}
                          >
                            {Math.round(maxVariacao)}%
                          </Text>
                        </Flex>
                      </div>

                      {/* Seções por codi_esp */}
                      <div className="flex flex-col gap-2 p-3">
                        {items.map(item => {
                          const variacaoTotal = item.variacao_notas_total ?? 0;
                          const espColor = getVariacaoColor(variacaoTotal);
                          const espBg = getVariacaoBg(variacaoTotal);

                          return (
                            <div
                              key={`${codiEmp}-${item.codi_esp}`}
                              className="rounded-md border p-2.5"
                              style={{ borderColor: espColor, backgroundColor: espBg }}
                            >
                              <div className="mb-2 flex items-center justify-between">
                                <Text fontSize="xs" fontWeight="bold" color="gray.700">
                                  {getCodEspLabel(item.codi_esp)}
                                </Text>
                                <Flex alignItems="center" gap={1}>
                                  {getVariacaoIcon(
                                    item.notas_total_atual,
                                    item.notas_total_anterior,
                                  )}
                                  <Text fontSize="xs" fontWeight="bold" color={espColor}>
                                    {Math.round(variacaoTotal)}%
                                  </Text>
                                </Flex>
                              </div>
                              <div className="grid grid-cols-4 gap-1.5">
                                <div className="rounded bg-white px-2 py-1">
                                  <Text fontSize="9px" fontWeight="bold" color="blue.600">
                                    SAÍDAS
                                  </Text>
                                  <Text fontSize="xs" color="gray.600">
                                    {item.saidas_anterior} → {item.saidas_atual}
                                  </Text>
                                  <Text fontSize="xs" fontWeight="bold">
                                    {Math.round(item.variacao_saidas)}%
                                  </Text>
                                </div>
                                <div className="rounded bg-white px-2 py-1">
                                  <Text fontSize="9px" fontWeight="bold" color="purple.600">
                                    SERVIÇOS
                                  </Text>
                                  <Text fontSize="xs" color="gray.600">
                                    {item.servicos_anterior} → {item.servicos_atual}
                                  </Text>
                                  <Text fontSize="xs" fontWeight="bold">
                                    {Math.round(item.variacao_servicos)}%
                                  </Text>
                                </div>
                                <div className="rounded bg-white px-2 py-1">
                                  <Text fontSize="9px" fontWeight="bold" color="green.600">
                                    ENTRADAS
                                  </Text>
                                  <Text fontSize="xs" color="gray.600">
                                    {item.entradas_anterior} → {item.entradas_atual}
                                  </Text>
                                  <Text fontSize="xs" fontWeight="bold">
                                    {Math.round(item.variacao_entradas)}%
                                  </Text>
                                </div>
                                <div className="rounded bg-white px-2 py-1">
                                  <Text fontSize="9px" fontWeight="bold" color="gray.600">
                                    TOTAL
                                  </Text>
                                  <Text fontSize="xs" color="gray.600">
                                    {item.notas_total_anterior} → {item.notas_total_atual}
                                  </Text>
                                  <Text fontSize="xs" fontWeight="bold">
                                    {Math.round(item.variacao_notas_total)}%
                                  </Text>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
              <Paginator pagination={pagination} />
            </>
          )}
        </div>
      </FormBox>
    </div>
  );
}
